import type { Withdrawal } from '../models';

export type WithdrawalPaymentAdjustmentReason = 'insufficientBalance' | 'partialApproval';

export interface WithdrawalPaymentAdjustment {
  approvedAmount: number;
  walletBalance: number;
  selectable: boolean;
  reason?: WithdrawalPaymentAdjustmentReason;
}

export const getWithdrawalWalletBalance = (withdrawal: Withdrawal): number => {
  if (withdrawal.wallet?.balance_total != null) return withdrawal.wallet.balance_total;
  if (withdrawal.wallet_balance != null) return withdrawal.wallet_balance;
  return 0;
};

const getRequestedAmount = (withdrawal: Withdrawal) => withdrawal.approved_amount ?? withdrawal.requested_amount;

export const computePaymentFileAmounts = (
  withdrawals: Withdrawal[],
  selectedIds: Set<number>,
): {
  approvedAmounts: Record<number, number>;
  adjustments: Map<number, WithdrawalPaymentAdjustment>;
} => {
  const walletRemaining = new Map<number, number>();
  const approvedAmounts: Record<number, number> = {};
  const adjustments = new Map<number, WithdrawalPaymentAdjustment>();

  for (const withdrawal of withdrawals) {
    if (!walletRemaining.has(withdrawal.wallet_id)) {
      walletRemaining.set(withdrawal.wallet_id, getWithdrawalWalletBalance(withdrawal));
    }
  }

  for (const withdrawal of withdrawals) {
    const walletBalance = getWithdrawalWalletBalance(withdrawal);
    const requested = getRequestedAmount(withdrawal);

    if (!selectedIds.has(withdrawal.id)) {
      adjustments.set(withdrawal.id, {
        approvedAmount: requested,
        walletBalance,
        selectable: walletBalance > 0,
        reason: walletBalance <= 0 ? 'insufficientBalance' : undefined,
      });
      continue;
    }

    const remaining = walletRemaining.get(withdrawal.wallet_id) ?? walletBalance;

    if (remaining <= 0) {
      adjustments.set(withdrawal.id, {
        approvedAmount: 0,
        walletBalance,
        selectable: false,
        reason: 'insufficientBalance',
      });
      continue;
    }

    const approvedAmount = Math.min(requested, remaining);
    walletRemaining.set(withdrawal.wallet_id, remaining - approvedAmount);
    approvedAmounts[withdrawal.id] = approvedAmount;

    adjustments.set(withdrawal.id, {
      approvedAmount,
      walletBalance,
      selectable: approvedAmount > 0,
      reason: approvedAmount < requested ? 'partialApproval' : undefined,
    });
  }

  return { approvedAmounts, adjustments };
};

export const computeInitialPaymentFileState = (withdrawals: Withdrawal[]) => {
  const allSelected = new Set(withdrawals.map((w) => w.id));
  const { adjustments: allAdjustments } = computePaymentFileAmounts(withdrawals, allSelected);

  const selectedIds = new Set<number>();
  for (const withdrawal of withdrawals) {
    const adjustment = allAdjustments.get(withdrawal.id);
    if (adjustment?.selectable && adjustment.approvedAmount > 0) {
      selectedIds.add(withdrawal.id);
    }
  }

  const { approvedAmounts, adjustments } = computePaymentFileAmounts(withdrawals, selectedIds);
  const excludedCount = withdrawals.filter((w) => !selectedIds.has(w.id)).length;
  const partialCount = [...adjustments.values()].filter((a) => a.reason === 'partialApproval').length;

  return { selectedIds, approvedAmounts, adjustments, excludedCount, partialCount };
};

export const canSelectWithdrawal = (
  withdrawals: Withdrawal[],
  selectedIds: Set<number>,
  withdrawalId: number,
): boolean => {
  if (selectedIds.has(withdrawalId)) return true;

  const withdrawal = withdrawals.find((w) => w.id === withdrawalId);
  if (!withdrawal || getWithdrawalWalletBalance(withdrawal) <= 0) return false;

  const nextSelectedIds = new Set(selectedIds);
  nextSelectedIds.add(withdrawalId);
  const { adjustments } = computePaymentFileAmounts(withdrawals, nextSelectedIds);
  const adjustment = adjustments.get(withdrawalId);

  return Boolean(adjustment?.selectable && adjustment.approvedAmount > 0);
};
